import type { SceneCtx } from "./scene.ts";

/** Keep letter shortcuts out of text editing and select controls. */
export function editingControl(target: EventTarget | null): boolean {
  return target instanceof HTMLElement &&
    (target.isContentEditable ||
      target.closest("input, textarea, select") !== null);
}

/** Shared controls for both run modes. The simulation pauses while the
 * camera can still orbit, and sharing always copies the complete seed URL. */
export function installRunControls(ctx: SceneCtx): void {
  const pause = document.getElementById("pause-run")!;
  const paused = document.getElementById("paused-label")!;
  const fullscreen = document.getElementById("fullscreen")!;
  const share = document.getElementById("share-run")!;
  const status = document.getElementById("share-status")!;
  let messageTimer: ReturnType<typeof setTimeout>;
  const message = (text: string) => {
    clearTimeout(messageTimer);
    status.textContent = text;
    messageTimer = setTimeout(() => status.textContent = "", 3500);
  };
  const setPaused = (value: boolean) => {
    ctx.paused = value;
    pause.textContent = ctx.paused ? "Resume" : "Pause";
    pause.setAttribute("aria-pressed", String(ctx.paused));
    paused.hidden = !ctx.paused;
  };
  const togglePause = () => setPaused(!ctx.paused);
  document.getElementById("new-track")?.addEventListener(
    "click",
    () => setPaused(false),
  );
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      message("Full screen is unavailable in this browser.");
    }
  };
  document.addEventListener("fullscreenchange", () => {
    const active = !!document.fullscreenElement;
    fullscreen.textContent = active ? "Exit full screen" : "Full screen";
    fullscreen.setAttribute("aria-pressed", String(active));
  });
  pause.addEventListener("click", togglePause);
  fullscreen.addEventListener("click", toggleFullscreen);
  share.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(location.href);
      message("World link copied. Send someone the same adventure.");
    } catch {
      message("Copy the address bar to share this world.");
    }
  });
  addEventListener("keydown", (e) => {
    if (
      editingControl(e.target) || e.repeat || e.metaKey || e.ctrlKey || e.altKey
    ) return;
    if (e.code === "KeyK") {
      e.preventDefault();
      togglePause();
    } else if (e.code === "KeyF") {
      e.preventDefault();
      void toggleFullscreen();
    }
  });
}
